import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppStore } from '@/lib/hooks';

import UnitCard from '@/common/components/course/display/UnitCard';
import CourseOverviewSkeleton from '../skeletons/CourseOverviewSkeleton';

import { ActivityData, CourseData, CourseId, SubunitData, UnitData } from 'course-types';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon } from '@/assets/icons/chevron';
import CourseUtilityService from '@/services/course/course-utility-service';

interface Props {
  onActivitySelect: (
    activity: ActivityData,
    subunit?: SubunitData & { id: string },
    unitId?: string,
  ) => void;
}

export default function MainCourseStudentView({ onActivitySelect }: Props) {
  const store = useAppStore();
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const [units, setUnits] = useState<Array<[string, UnitData]>>([]);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [expandAllCounter, setExpandAllCounter] = useState(0);
  const [collapseAllCounter, setCollapseAllCounter] = useState(0);

  // Calculate total activities across all units
  const totalActivities = CourseUtilityService.calculateTotalActivities(units);
  const totalResources = CourseUtilityService.calculateTotalResources(units);

  useEffect(() => {
    setCourseUnits();
  }, [activeCourseData]);

  useEffect(() => {
    const initialExpandedState = units.reduce(
      (acc, unit) => {
        acc[unit[0]] = true;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    setExpanded(initialExpandedState);
  }, [units]);

  //assign state from course-map cookie data
  const setCourseUnits = async (): Promise<void> => {
    if (activeCourseData != undefined) {
      const unitsArray = Object.entries(
        activeCourseData.units ? activeCourseData?.units : [],
      ) as Array<[string, UnitData]>;

      unitsArray.sort((unitA, unitB) => unitA[1].index - unitB[1].index);
      setUnits(unitsArray);
    }
  };

  const handleCollapseAll = () => {
    setCollapseAllCounter((c) => c + 1);
    const newExpandedState = units.reduce(
      (acc, unit) => {
        acc[unit[0]] = false;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    setExpanded(newExpandedState);
  };

  const handleExpandAll = () => {
    setExpandAllCounter((c) => c + 1);
    const newExpandedState = units.reduce(
      (acc, unit) => {
        acc[unit[0]] = true;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    setExpanded(newExpandedState);
  };

  const handleExpandedChange = (unitId: string, isExpanded: boolean) => {
    setExpanded((prev) => ({ ...prev, [unitId]: isExpanded }));
  };

  if (activeCourseData.id === ('' as CourseId)) {
    return <CourseOverviewSkeleton />;
  }

  return (
    <div className='w-full flex flex-col max-w-6xl mx-auto px-6 gap-4'>
      {/* Back to Courses */}
      <button
        type='button'
        onClick={() => window.history.back()}
        className='flex items-center gap-1.5 w-fit text-xs font-semibold mb-1'
        style={{ color: '#1570EF' }}
      >
        <ChevronLeftIcon className='w-4 h-4' />
        Back to Courses
      </button>

      {/* Header with controls */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          {units.length > 0 && (
            <button
              onClick={
                units.every(([unitId]) => expanded[unitId] === true)
                  ? handleCollapseAll
                  : handleExpandAll
              }
              className='flex items-center gap-2 px-4 py-2 text-xs font-semibold'
              style={{
                borderRadius: '8px',
                border: '1px solid #D5D7DA',
                background: '#FFF',
                boxShadow: '0 1px 3px 0 rgba(10, 13, 18, 0.10), 0 1px 2px 0 rgba(10, 13, 18, 0.06)',
                color: '#374151',
              }}
            >
              {units.every(([unitId]) => expanded[unitId] === true) ? (
                <>
                  <ChevronUpIcon className='w-4 h-4' />
                  Collapse all
                </>
              ) : (
                <>
                  <ChevronDownIcon className='w-4 h-4' />
                  Expand all
                </>
              )}
            </button>
          )}
        </div>
        <p className='flex flex-col md:flex-row items-center gap-1 text-xs font-semibold text-text_gray_600'>
          <span className='text-xs font-semibold text-text_gray_600'>{units.length} units</span>
          <span className='text-xs font-semibold text-text_gray_600 hidden md:block'>•</span>
          <span className='text-xs font-semibold text-text_gray_600'>
            {totalActivities} activit{totalActivities === 1 ? 'y' : 'ies'}
          </span>
          {totalResources > 0 && (
            <>
              <span className='text-xs font-semibold text-text_gray_600 hidden md:block'>•</span>
              <span className='text-xs font-semibold text-text_gray_600'>{`${totalResources} ${totalResources === 1 ? 'resource' : 'resources'}`}</span>
            </>
          )}
        </p>
      </div>

      {/* Units Grid */}
      <div className='space-y-6'>
        {units.map((unit, index) => (
          <UnitCard
            key={unit[0]}
            unit={unit[1]}
            unitId={unit[0]}
            initialExpanded={expanded[unit[0]] || false}
            onExpandedChange={handleExpandedChange}
            expandAll={expandAllCounter}
            collapseAll={collapseAllCounter}
            onClick={onActivitySelect}
          />
        ))}
      </div>
      <div className='h-16' />
    </div>
  );
}
